import { useEffect, useRef, useState } from "react";
import { HexAlphaColorPicker, HexColorInput } from "react-colorful";
import { BaseInput, Button, Card, getInputClass, Label } from "@osago/ui";

interface ColorFieldProps {
  label: string;
  value: string;
  fallback: string;
  onChange: (value: string) => void;
}

const CHECKER =
  "repeating-conic-gradient(#c9ced8 0% 25%, #ffffff 0% 50%) 50% / 8px 8px";

const POPUP_HEIGHT = 330;

export function ColorField({ label, value, fallback, onChange }: ColorFieldProps) {
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  function toggle() {
    if (!open && ref.current) {
      const { bottom } = ref.current.getBoundingClientRect();
      setDropUp(window.innerHeight - bottom < POPUP_HEIGHT);
    }
    setOpen((v) => !v);
  }

  const effective = value || fallback;

  return (
    <div ref={ref} className="relative">
      <div className="flex items-center justify-between gap-3 rounded-input border border-input-border bg-field-bg px-3 py-2.5">
        <Label variant="body" as="span" className="min-w-0">
          {label}
        </Label>
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0 border-input-border! text-label-text"
          onClick={toggle}
          leftIcon={
            <span
              className="h-4 w-4 rounded-sm border border-input-border"
              style={{ background: CHECKER }}
            >
              <span className="block h-full w-full rounded-sm" style={{ background: effective }} />
            </span>
          }
        >
          <span className="font-mono uppercase">{value || "по умолчанию"}</span>
        </Button>
      </div>

      {open && (
        <Card
          padding="sm"
          className={`absolute right-0 left-0 z-20 [&_.react-colorful]:h-45! [&_.react-colorful]:w-full! ${
            dropUp ? "bottom-full mb-2" : "top-full mt-2"
          }`}
        >
          <Label variant="body" className="mb-2">{label}</Label>
          <HexAlphaColorPicker color={effective} onChange={onChange} />
          <div className="mt-3 flex items-center gap-2">
            <BaseInput className="flex-1" leftSlot={<span className="text-label-text">#</span>}>
              <HexColorInput
                color={effective}
                onChange={onChange}
                alpha
                prefixed={false}
                type="text"
                aria-label={`HEX-код: ${label}`}
                className={`${getInputClass("sm")} font-mono uppercase`}
              />
            </BaseInput>
            <Button variant="link" className="text-xs" onClick={() => onChange("")}>
              Сбросить
            </Button>
          </div>

          <Button
            variant="secondary"
            size="sm"
            fullWidth
            className="mt-2 justify-start"
            leftIcon={
              <span
                className="h-3.5 w-3.5 rounded-sm border border-input-border"
                style={{ background: fallback }}
              />
            }
            onClick={() => onChange(fallback)}
          >
            Текущий цвет
            <span className="ml-auto font-mono uppercase">{fallback}</span>
          </Button>
        </Card>
      )}
    </div>
  );
}
